import {constant} from '@packages/utilities/fp/utils';
import {matchType, Type} from '@packages/systems/wfdl';
import {cacheMemo, weakMemo} from '@packages/utilities/memo';

const BRAND = Symbol('SerializedType');
export type SerializedType = string & {readonly [BRAND]: true};

const typeVal = (tag: string, inner: string) =>
  `{"type":"${tag}","val":${inner}}` as SerializedType;

const stringify = JSON.stringify as (v: unknown) => string;

const ELEMENT = '{"type":"Element"}' as SerializedType;
const ROW_EMPTY = '{"type":"RowEmpty"}' as SerializedType;

// Serializes a Type object into a JSON string.
export const serializeType: (t: Type) => SerializedType = weakMemo(
  matchType({
    Element: constant(ELEMENT),

    Generic: ({arg, body}) =>
      typeVal(
        'Generic',
        `{"arg":${stringify(arg)},"body":${serializeType(body)}}`
      ),

    Rigid: (id) => typeVal('Rigid', stringify(id)),

    RowCons: ({label, type, tail}) =>
      typeVal(
        'RowCons',
        `{"label":${stringify(label)},"type":${serializeType(type)},"tail":${serializeType(tail)}}`
      ),

    RowEmpty: constant(ROW_EMPTY),

    TypeApplication: ({con, arg}) =>
      typeVal(
        'TypeApplication',
        `{"con":${serializeType(con)},"arg":${serializeType(arg)}}`
      ),

    TypeConstructor: (name) => typeVal('TypeConstructor', stringify(name)),

    TypeReference: (name) => typeVal('TypeReference', stringify(name)),

    Unknown: ({id, rigids}) =>
      typeVal(
        'Unknown',
        `{"id":${stringify(id)},"rigids":${stringify(rigids)}}`
      ),
  })
);

const cache100 = cacheMemo(100);

// Unserializes a JSON string back into a Type object.
export const unserializeType: (SerializedType: SerializedType) => Type =
  cache100((SerializedType: SerializedType) => JSON.parse(SerializedType));
